import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import type {
  CallPolicyConfig,
  PremiseConfig,
  ResolvedCallPolicy,
} from "@shared/types/foyer";
import {
  DEFAULT_CALL_ANNOUNCE_SECONDS,
  DEFAULT_CALL_RING_TIMEOUT_SEC,
  resolveCallPolicy,
} from "@shared/types/foyer";

/**
 * Config → Video calls · the premise calling policy (VC-09/VC-12).
 *
 * A CONTROLLED editor: it holds no server state and has no Save button of its
 * own. Every change is pushed straight into the parent premise draft
 * (`care.calls`), so the one top-bar Save persists calling alongside everything
 * else on the premise, and toggling it flips the shared "unsaved changes"
 * indicator like any other field.
 *
 * Auto-answer keeps its friction: it is a permission to open a camera in a
 * bedroom without anyone pressing accept, so arming it opens a confirmation
 * that requires a separate acknowledgement tick. Turning it OFF is instant.
 */

interface Props {
  premise: PremiseConfig;
  /** Push edits into the parent premise draft (saved by the top-bar Save). */
  onChange: (next: PremiseConfig) => void;
}

/** Editable mirror of the policy. Numbers live as strings while being typed. */
interface Draft {
  enabled: boolean;
  residentsMayCallOut: boolean;
  allowAutoAnswer: boolean;
  quietFrom: string;
  quietTo: string;
  quietTz: string;
  ringTimeoutSec: string;
  maxCallDurationMin: string;
  announceSeconds: string;
}

function toDraft(p: ResolvedCallPolicy, fallbackTz: string): Draft {
  return {
    enabled: p.enabled,
    residentsMayCallOut: p.residentsMayCallOut,
    allowAutoAnswer: p.allowAutoAnswer,
    quietFrom: p.quietHours?.from ?? "",
    quietTo: p.quietHours?.to ?? "",
    quietTz: p.quietHours?.tz ?? fallbackTz,
    ringTimeoutSec: String(p.ringTimeoutSec),
    maxCallDurationMin:
      p.maxCallDurationMin === null ? "" : String(p.maxCallDurationMin),
    announceSeconds: String(p.announceSeconds),
  };
}

/** The draft as a full policy block, ready to store in `care.calls`. Sending
 *  the whole block (not a patch) means an untouched field can't stay armed from
 *  a previous save. */
function draftToConfig(d: Draft): CallPolicyConfig {
  const int = (v: string, fallback: number) => {
    const n = Number(v);
    return v.trim() && Number.isFinite(n) ? Math.trunc(n) : fallback;
  };
  const quiet =
    d.quietFrom.trim() && d.quietTo.trim()
      ? { from: d.quietFrom.trim(), to: d.quietTo.trim(), tz: d.quietTz.trim() }
      : null;
  return {
    enabled: d.enabled,
    residentsMayCallOut: d.residentsMayCallOut,
    allowAutoAnswer: d.allowAutoAnswer,
    quietHours: quiet,
    ringTimeoutSec: int(d.ringTimeoutSec, DEFAULT_CALL_RING_TIMEOUT_SEC),
    maxCallDurationMin: d.maxCallDurationMin.trim()
      ? int(d.maxCallDurationMin, 0)
      : null,
    announceSeconds: int(d.announceSeconds, DEFAULT_CALL_ANNOUNCE_SECONDS),
  };
}

/** Field-level complaints, keyed by field. Empty = clean. */
function validate(d: Draft): Record<string, string> {
  const errs: Record<string, string> = {};
  const partialQuiet =
    [d.quietFrom, d.quietTo].filter((v) => v.trim()).length === 1;
  if (partialQuiet) {
    errs.quiet = "Set both a start and an end time, or clear both.";
  } else if (d.quietFrom.trim() && !d.quietTz.trim()) {
    errs.quiet = "Quiet hours need a timezone.";
  }
  const ring = Number(d.ringTimeoutSec);
  if (!Number.isInteger(ring) || ring < 5 || ring > 300) {
    errs.ringTimeoutSec = "5-300 seconds.";
  }
  const announce = Number(d.announceSeconds);
  if (!Number.isInteger(announce) || announce < 0 || announce > 60) {
    errs.announceSeconds = "0-60 seconds.";
  }
  if (d.maxCallDurationMin.trim()) {
    const max = Number(d.maxCallDurationMin);
    if (!Number.isInteger(max) || max < 1 || max > 24 * 60) {
      errs.maxCallDurationMin = "1-1440 minutes, or leave empty for no limit.";
    }
  }
  return errs;
}

export default function CallPolicyEditor({ premise, onChange }: Props) {
  const isSeniorLiving = premise.type === "SENIOR_LIVING";
  const fallbackTz = premise.address?.timezone ?? "Europe/Stockholm";

  const [draft, setDraft] = useState<Draft>(() =>
    toDraft(resolveCallPolicy(premise.care), fallbackTz)
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const errors = useMemo(() => validate(draft), [draft]);
  const effective = useMemo(() => draftToConfig(draft), [draft]);

  /** Update local edit state AND push the resolved block into the parent draft
   *  so the top-bar Save picks it up. */
  const set = (patch: Partial<Draft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    onChange({
      ...premise,
      care: { ...(premise.care ?? {}), calls: draftToConfig(next) },
    });
  };

  /**
   * The switch never flips straight to on: arming opens the confirmation and
   * leaves the draft untouched until it is acknowledged. Disarming is immediate.
   */
  const onAutoAnswerToggle = (nextOn: boolean) => {
    if (!nextOn) {
      set({ allowAutoAnswer: false });
      return;
    }
    setAcknowledged(false);
    setConfirmOpen(true);
  };

  const confirmAutoAnswer = () => {
    if (!acknowledged) return;
    set({ allowAutoAnswer: true });
    setConfirmOpen(false);
  };

  return (
    <Stack spacing={3} data-testid="call-policy">
      {/* ── Master switch ───────────────────────────────────────────────── */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Video calling
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={draft.enabled}
                onChange={(e) => set({ enabled: e.target.checked })}
                inputProps={
                  { "data-testid": "policy-enabled" } as Record<string, string>
                }
              />
            }
            label="Video calling enabled"
          />
          {/* VC2-8: there used to be a second, template-side switch for the
              tile, and "policy on, no tile" was the result. Say plainly what
              this switch now decides, so nobody goes looking for the other. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, ml: 1.75 }}
            data-testid="policy-enabled-help"
          >
            With this on, the Videosamtal tile appears by itself on the TV of
            every resident who holds a calling seat - there is no separate
            template switch. Seats are assigned per resident on the Residents
            page.
          </Typography>
        </CardContent>
      </Card>

      {/* ── Call rules - only when calling is on ─────────────────────────── */}
      {draft.enabled && (
        <Card variant="outlined" data-testid="call-rules-card">
          <CardContent>
            <Stack spacing={2.5}>
              <Typography variant="h6">Call rules</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={draft.residentsMayCallOut}
                    onChange={(e) =>
                      set({ residentsMayCallOut: e.target.checked })
                    }
                    inputProps={
                      { "data-testid": "policy-call-out" } as Record<
                        string,
                        string
                      >
                    }
                  />
                }
                label="Residents may call out from their room"
              />

              <FormControlLabel
                control={
                  <Switch
                    color="warning"
                    checked={draft.allowAutoAnswer}
                    onChange={(e) => onAutoAnswerToggle(e.target.checked)}
                    inputProps={
                      { "data-testid": "policy-auto-answer" } as Record<
                        string,
                        string
                      >
                    }
                  />
                }
                label="Allow staff auto-answer"
              />

              {/* ── Quiet hours ─────────────────────────────────────────── */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Quiet hours
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    size="small"
                    type="time"
                    label="From"
                    value={draft.quietFrom}
                    onChange={(e) => set({ quietFrom: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ "data-testid": "policy-quiet-from" }}
                  />
                  <TextField
                    size="small"
                    type="time"
                    label="To"
                    value={draft.quietTo}
                    onChange={(e) => set({ quietTo: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ "data-testid": "policy-quiet-to" }}
                  />
                  <TextField
                    size="small"
                    label="Timezone"
                    value={draft.quietTz}
                    onChange={(e) => set({ quietTz: e.target.value })}
                    inputProps={{ "data-testid": "policy-quiet-tz" }}
                  />
                </Stack>
                {errors.quiet && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mt: 1 }}
                    data-testid="policy-quiet-help"
                  >
                    {errors.quiet}
                  </Typography>
                )}
              </Box>

              {/* ── Timings ─────────────────────────────────────────────── */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  size="small"
                  type="number"
                  label="Ring timeout (seconds)"
                  value={draft.ringTimeoutSec}
                  onChange={(e) => set({ ringTimeoutSec: e.target.value })}
                  error={!!errors.ringTimeoutSec}
                  helperText={errors.ringTimeoutSec}
                  inputProps={{ "data-testid": "policy-ring-timeout" }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Max call length (minutes)"
                  value={draft.maxCallDurationMin}
                  onChange={(e) => set({ maxCallDurationMin: e.target.value })}
                  error={!!errors.maxCallDurationMin}
                  helperText={errors.maxCallDurationMin}
                  inputProps={{ "data-testid": "policy-max-duration" }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Announcement (seconds)"
                  value={draft.announceSeconds}
                  onChange={(e) => set({ announceSeconds: e.target.value })}
                  error={!!errors.announceSeconds}
                  helperText={errors.announceSeconds}
                  inputProps={{ "data-testid": "policy-announce" }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Arming friction ─────────────────────────────────────────────── */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>
          Allow staff to open a camera without anyone accepting?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Turning this on means a staff contact marked <em>auto-answer</em>{" "}
            can start a call into any room at{" "}
            <strong>{premise.premiseName}</strong> and be connected
            automatically after a {effective.announceSeconds}-second spoken
            announcement. Nobody in the room has to press accept.
          </Typography>
          {isSeniorLiving && (
            <Alert
              severity="error"
              sx={{ mb: 1.5 }}
              data-testid="confirm-senior-warning"
            >
              This is a senior living premise. A resident’s room is their home,
              and this is <strong>not recommended</strong> here.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            It stays inert for family and service contacts - only staff contacts
            you separately mark as auto-answer are affected.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                inputProps={
                  { "data-testid": "confirm-auto-answer-ack" } as Record<
                    string,
                    string
                  >
                }
              />
            }
            label="I understand this lets staff see and hear into a room without consent in the moment."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!acknowledged}
            onClick={confirmAutoAnswer}
            data-testid="confirm-auto-answer"
          >
            Allow auto-answer
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
